<template>
    <div v-if="matches.length" class="flex-col justify-start items-start flex">
        <div class="self-stretch pb-2 justify-start items-center gap-2 flex">
            <div class="text-slate-600 dark:text-slate-300 text-sm font-medium">Last matches</div>
            <div
                class="gap-1 flex text-slate-500 dark:text-slate-400 text-xs font-normal leading-none flex items-center"
            >
                Updated {{ lastUpdatedAt }}
                <span>
                    <Tooltip
                        text="The availability data that is presented is refreshed periodically and might not be 100% up-to-date. Please verify availability with the hotel prior to transferring any points."
                    />
                </span>
            </div>
        </div>
        <ul class="w-full">
            <li
                v-for="(match, index) in matches"
                :key="`match-${index}`"
                class="flex justify-between items-center py-4 first:pt-0 last:pb-0 gap-2 lg:gap-6 @container"
            >
                <div
                    class="flex flex-col @lg:grow lg:items-center justify-start @lg:justify-between @lg:flex-row @lg:gap-8"
                >
                    <div class="flex gap-2">
                        <Icon icon="bed" class="hidden @lg:block" />
                        <p
                            class="text-slate-800 dark:text-slate-100 text-base font-medium mb-1 lg:mb-0"
                        >
                            {{ alertDatesString }}
                        </p>
                    </div>

                    <p
                        class="text-slate-800 dark:text-slate-100 text-sm lg:text-lg font-medium @lg:text-right"
                    >
                        {{
                            match.min_points
                                ? match.min_points.toLocaleString('en-US')
                                : 'NA'
                        }}
                        points
                        <OrFor :item="match" :nights="alert.number_of_nights" />
                    </p>
                </div>
                <div class="flex justify-end">
                    <div
                        class="px-4 py-2.5 bg-orange-600 rounded shadow justify-center items-center gap-2 flex"
                    >
                        <router-link
                            class="text-white text-sm font-medium leading-tight"
                            :to="availabilityUrlParams"
                            @click="handleLinkClick"
                        >
                            Book
                        </router-link>
                    </div>
                </div>
            </li>
        </ul>
    </div>
    <div v-else>
        <EmptyState
            :hasAction="false"
            title="No matching availability"
            description="Once matching availability is found, it will appear here. Expired matches are not shown."
        />
    </div>
</template>

<script>
import { storeToRefs } from 'pinia';
import { useAlertsStore } from '@/store/alerts';
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import Tooltip from '@/components/tooltip.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import OrFor from '@/components/calendar/partials/or-for.vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

export default {
    components: {
        EmptyState,
        Icon,
        LoadingState,
        OrFor,
        Tooltip,
    },
    props: {
        alert: Object,
    },
    setup(props) {
        const store = useAlertsStore();
        const { loadingMatches } = storeToRefs(store);
        // The alert object coming from store.activeAlert or alert prop
        const matches = computed(() => props.alert.matches);
        const lastUpdatedAt = computed(() => {
            if (!props.alert.matches.length) return '--';
            return dayjs(props.alert.matches[0].updated_at).fromNow();
        });

        const formatDate = (dateStr) => {
            return dayjs(dateStr).format('MMM D');
        };

        const availabilityUrlParams = computed(() => {
            if (matches.value.length) {
                return {
                    name: 'HotelProperty',
                    params: { slug: props.alert.property.slug },
                    query: {
                        check_in: props.alert.matches[0].start_date,
                        check_out: props.alert.matches[0].end_date,
                    },
                };
            }
        });

        const alertDatesString = computed(() => {
            const { alert_type, check_in, check_out, number_of_nights } =
                props.alert;
            const startDate = dayjs(check_in);
            const endDate = dayjs(check_out);

            if (alert_type === 'flexible') {
                const monthYear = startDate.format('MMMM YYYY');
                const formattedNights =
                    number_of_nights > 1 ? 'nights' : 'night';
                return `${monthYear}, ${number_of_nights} ${formattedNights}`;
            } else {
                const start = startDate.format('MMM DD');
                const end = endDate.format('MMM DD');
                const duration = endDate.diff(startDate, 'day');
                const formattedDuration = duration > 1 ? 'nights' : 'night';
                return `${start} - ${end}, ${duration} ${formattedDuration}`;
            }
        });

        const handleLinkClick = () => {
            store.setShowAlertModal(false);
        };

        return {
            alertDatesString,
            availabilityUrlParams,
            formatDate,
            handleLinkClick,
            matches,
            loadingMatches,
            lastUpdatedAt,
        };
    },
};
</script>
